import React, { useState } from "react";
import Navbar from "../components/common/Navbar";
import Alert from "../components/common/Alert";
import DisclaimerModal from "../components/common/DisclaimerModal";
import { CONTACT_ENDPOINTS } from "../utils/constants";
import { isValidEmail } from "../utils/validators";

import "../styles/global.css";
import "../styles/pages/cryptoConfessions.css";

const ALERT_DURATION = 6000;
const ADMIN_EMAIL = "annabel@bitmobiletech.com";

const CryptoConfessions = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    story: "",
    marketingOptIn: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [alert, setAlert] = useState({
    isOpen: false,
    title: "",
    message: "",
    footer: "",
    type: "success"
  });

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, isOpen: false }));
  };

  const showAlert = (title, message, type = "success", footer = "") => {
    setAlert({
      isOpen: true,
      title,
      message,
      footer,
      type
    });

    setTimeout(() => {
      setAlert((prev) => ({ ...prev, isOpen: false }));
    }, ALERT_DURATION);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const validateForm = () => {
    const email = formData.email.trim();
    const story = formData.story.trim();

    if (!email || !story) {
      showAlert("Missing information", "Email and story are required fields.", "fail");
      return false;
    }

    if (!isValidEmail(email)) {
      showAlert("Invalid email", "Please enter a valid email address.", "fail");
      return false;
    }

    if (story.length < 10) {
      showAlert("Tell us more", "Please share a bit more detail so we can feature your story.", "fail");
      return false;
    }

    return true;
  };

  const buildAdminMessage = () => {
    const name = formData.name.trim() || "Anonymous";
    return [
      "A new Crypto Confession has been submitted.",
      "",
      `Name: ${name}`,
      `Email: ${formData.email.trim()}`,
      `Marketing Opt-In: ${formData.marketingOptIn ? "Yes" : "No"}`,
      "",
      "Story:",
      formData.story.trim()
    ].join("\n");
  };

  const buildConfirmationMessage = () => {
    const name = formData.name.trim() || "there";
    return [
      `Hi ${name},`,
      "",
      "Thanks for sharing your crypto story with PhēnixLoc! Your confession is in and you are officially entered for this month's giveaway.",
      "",
      "We'll announce the winner at the end of the month and reach out by email within 7 days if you are selected.",
      "",
      "In the meantime, keep your digital life locked down with PhēnixLoc's multi-layer security and decentralized protection.",
      "",
      "Good luck!",
      "The PhēnixLoc Team"
    ].join("\n");
  };

  const sendEmail = async (payload) => {
    const response = await fetch(CONTACT_ENDPOINTS.CONTACT_SUPPORT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.success === false) {
      const message = data.error || data.message || "Unable to send email.";
      throw new Error(message);
    }

    return data;
  };

  const saveToGoogleSheet = async () => {
    try {
      const response = await fetch(CONTACT_ENDPOINTS.GOOGLE_SHEETS, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name.trim() || "Anonymous",
          email: formData.email.trim(),
          story: formData.story.trim(),
          marketingOptIn: formData.marketingOptIn
        })
      });
      
      console.log("Saved to Google Sheets");
    } catch (error) {
      console.error("Error saving to Google Sheets:", error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const adminPayload = {
      recipientEmail: ADMIN_EMAIL,
      targetEmail: ADMIN_EMAIL,
      subject: "New Crypto Confession",
      name: formData.name.trim() || "Anonymous",
      email: formData.email.trim(),
      message: buildAdminMessage(),
      context: "crypto-confession"
    };

    const confirmationPayload = {
      recipientEmail: formData.email.trim(),
      targetEmail: formData.email.trim(),
      subject: "We received your Crypto Confession!",
      name: formData.name.trim() || "PhēnixLoc Explorer",
      email: ADMIN_EMAIL,
      message: buildConfirmationMessage(),
      context: "crypto-confession-confirmation"
    };

    try {
      await Promise.all([
        saveToGoogleSheet(),
        sendEmail(adminPayload),
        sendEmail(confirmationPayload)
      ]);

      setFormData({ name: "", email: "", story: "", marketingOptIn: false });

      showAlert(
        "Confession submitted",
        "Thanks for sharing your story! Check your inbox for confirmation.",
        "success"
      );
    } catch (error) {
      console.error("Error sending crypto confession:", error);
      showAlert(
        "Submission failed",
        error.message || "We could not submit your confession. Please try again shortly.",
        "fail"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDisclaimer = (e) => {
    e.preventDefault();
    setIsDisclaimerOpen(true);
  };

  return (
    <>
      <Navbar />

      <section className="crypto-confessions-page">
        <div className="crypto-confessions-hero">
          <h1>Share your Crypto Confession for a chance to win a free PhēnixLoc plan.</h1>
          <p>Your Crypto Confession could win you free decentralized security. Share yours today!</p>
        </div>

        <div className="crypto-confessions-body">
          <div className="crypto-confessions-intro">
            <p>
              Lost a wallet? Forgot your seed phrase? You&rsquo;re not alone. Share your funniest,
              wildest, or most relatable crypto mishap for a chance to win a free PhēnixLoc plan.
            </p>
            <p>
              With PhēnixLoc, your digital life stays secure, private, and stress-free. Enjoy military-grade
              encryption, triple-layer protection, and a decentralized global network that keeps your data safe
              from every angle. Don&rsquo;t let a lost seed phrase slow you down, turn your confession into a win today!
            </p>
            <p>To get started, fill out the short form below.</p>
          </div>

          <form className="crypto-confessions-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Name (optional)</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name or alias"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group required">
              <label htmlFor="email">Email (for prize delivery + updates)</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group required">
              <label htmlFor="story">Your story</label>
              <textarea
                id="story"
                name="story"
                rows="6"
                value={formData.story}
                onChange={handleChange}
                placeholder="Tell us what happened..."
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="marketingOptIn"
                  checked={formData.marketingOptIn}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <span>Opt in to receive marketing updates</span>
              </label>
            </div>

            <div className="form-agreement">
              <p>
                By submitting, you agree to the{" "}
                <a href="#" onClick={openDisclaimer} className="disclaimer-link">
                  Official Giveaway Terms & Privacy Disclaimer
                </a>
                .
              </p>
            </div>

            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit My Crypto Confession"}
            </button>
          </form>

          <div className="crypto-confessions-disclaimer">
            <h2>Privacy & Giveaway Notice</h2>
            <p>
              We'll never share your name or email. Stories will be shared anonymously, so please don't include any private info like your wallet ID, seed phrase, or anything that could compromise your security. For more information, read our{" "}
              <a href="#" onClick={openDisclaimer} className="disclaimer-link">
                Official Giveaway Terms & Privacy Disclaimer
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <Alert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        title={alert.title}
        message={alert.message}
        footer={alert.footer}
        type={alert.type}
      />

      <DisclaimerModal 
        isOpen={isDisclaimerOpen} 
        onClose={() => setIsDisclaimerOpen(false)} 
      />
    </>
  );
};

export default CryptoConfessions;